/* import __COLOCATED_TEMPLATE__ from './shopify-install-messenger-modal.hbs'; */
/* RESPONSIBLE TEAM: team-self-serve */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { task } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import type { TaskGenerator } from 'ember-concurrency';
import type IntlService from 'embercom/services/intl';
import type AppService from 'embercom/services/app-service';
import type RouterService from '@ember/routing/router-service';
import type NotificationsService from 'embercom/services/notifications-service';
import { get } from 'embercom/lib/ajax';
import { captureException } from '@sentry/browser';

interface Args {
  onModalClose: () => void;
}

interface Signature {
  Args: Args;
  Element: never;
}

interface IntercomEventService {
  trackAnalyticsEvent(event: Record<string, unknown>): void;
}

type VerifyInstallResponse = {
  anonymous_request_at: string | null;
  identified_request_at: string | null;
};

export default class ShopifyInstallMessengerModal extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare appService: AppService;
  @service declare notificationsService: InstanceType<typeof NotificationsService>;
  @service declare intercomEventService: IntercomEventService;
  @service declare router: RouterService;

  @tracked checkedAtLeastOnce = false;
  @tracked isMessengerInstalled = false;

  get isChecking() {
    return taskFor(this.checkInstallationTask).isRunning;
  }

  get showSuccessBanner() {
    return !this.isChecking && this.isMessengerInstalled && this.checkedAtLeastOnce;
  }

  get showFailureBanner() {
    return !this.isChecking && !this.isMessengerInstalled && this.checkedAtLeastOnce;
  }

  @action
  openAdvancedSettings() {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'advanced_settings',
      place: 'shopify-onboarding-modal',
      section: 'getting-started',
      platform: 'web',
    });
    this.args.onModalClose();
    this.router.transitionTo('apps.app.settings.channels.messenger.install');
  }

  @action
  checkInstallation() {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'check_messenger_installation',
      place: 'shopify-onboarding-modal',
      section: 'getting-started',
      platform: 'web',
    });
    taskFor(this.checkInstallationTask).perform();
  }

  @task({ restartable: true })
  *checkInstallationTask(): TaskGenerator<void> {
    try {
      let response: VerifyInstallResponse = yield get(
        `/ember/messenger_install_verification/verify_any_install?app_id=${this.appService.app.id}`,
      );

      this.isMessengerInstalled = !!(
        response.anonymous_request_at || response.identified_request_at
      );
      this.checkedAtLeastOnce = true;
    } catch (error: unknown) {
      captureException(error, {
        tags: {
          responsibleTeam: 'team-self-serve',
        },
      });
      this.notificationsService.notifyError(
        this.intl.t(
          'apps.app.settings.installation.installation.new.installation-status.manual-check-error-message',
        ),
      );
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Onboarding::GettingStarted::ShopifyInstallMessengerModal': typeof ShopifyInstallMessengerModal;
  }
}
